import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Paper,
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem as MenuItemMUI,
  Card,
  CardContent,
  Grid,
  Button,
} from '@mui/material';
import {
  LocalHospital,
  ArrowBack,
  ExitToApp,
  Person,
} from '@mui/icons-material';
import { appointmentService, authService } from '../../services/api';

const OPDPage = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [anchorEl, setAnchorEl] = useState(null);
  const [appointments, setAppointments] = useState([]);

  useEffect(() => {
    loadTodayAppointments();
  }, []);

  const loadTodayAppointments = async () => {
    try {
      const response = await appointmentService.getTodayAppointments();
      
      // Filter for OPD appointments that are NOT Completed.
      // Doctors only see their own appointments; Admins see all.
      const opdAppointments = response.data.filter(apt =>
        apt.type === 'OPD' &&
        apt.status !== 'Completed' &&
        (user?.role !== 'Doctor' || apt.doctorId === user?.id)
      );
      
      // Sort appointments by appointment time
      const sortedAppointments = opdAppointments.sort((a, b) => {
        // Convert time strings to comparable format (HH:MM)
        const timeA = a.appointmentTime || '00:00';
        const timeB = b.appointmentTime || '00:00';
        return timeA.localeCompare(timeB);
      });
      
      setAppointments(sortedAppointments);
    } catch (error) {
      console.error('Error loading appointments:', error);
    }
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/');
  };

  const handleSelectPatient = (appointment) => {
    navigate(`/patient/${appointment.patientId}/${appointment.id}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <AppBar position="static" className="medical-gradient">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <LocalHospital sx={{ fontSize: 40, mr: 2 }} />
          <Typography variant="h5" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            OPD - Outpatient Department
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1">{user?.firstName}</Typography>
            <IconButton size="large" onClick={handleMenu} color="inherit">
              <Avatar sx={{ bgcolor: '#1565c0' }}>
                {user?.firstName?.charAt(0)}
              </Avatar>
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
              <MenuItemMUI onClick={handleLogout}>
                <ExitToApp sx={{ mr: 1 }} />
                Logout
              </MenuItemMUI>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Today's OPD Appointments
          </Typography>

          {appointments.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No appointments scheduled for today
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {appointments.map((appointment) => (
                <Grid item xs={12} sm={6} md={4} key={appointment.id}>
                  <Card
                    className="card-hover"
                    sx={{
                      cursor: 'pointer',
                      height: '100%',
                      '&:hover': {
                        boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                      },
                    }}
                    onClick={() => handleSelectPatient(appointment)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ bgcolor: '#2196f3', mr: 2 }}>
                          <Person />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {appointment.patient?.firstName} {appointment.patient?.lastName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Patient ID: {appointment.patientId}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Time:</strong> {appointment.appointmentTime}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Age:</strong> {appointment.patient?.age || 'N/A'}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Gender:</strong> {appointment.patient?.gender || 'N/A'}
                        </Typography>
                        {appointment.notes && (
                          <Typography variant="body2" sx={{ mt: 2 }}>
                            <strong>Chief Complaint:</strong> {appointment.notes}
                          </Typography>
                        )}
                      </Box>

                      <Button
                        fullWidth
                        variant="contained"
                        sx={{ mt: 2 }}
                        onClick={() => handleSelectPatient(appointment)}
                      >
                        Consult Patient
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      </Container>
    </div>
  );
};

export default OPDPage;
